<template>
  <aside v-if="open" class="sb-panel" aria-label="兑换沙箱">
    <header class="sb-head">
      <strong>本地沙箱</strong>
      <span>只拦 PLUS-SB / PRO20X-SB，真卡仍走线上</span>
      <button type="button" class="sb-x" @click="open = false">收起</button>
    </header>
    <div class="sb-tools">
      <label>时钟</label>
      <button
        v-for="n in speeds"
        :key="n"
        type="button"
        :class="{ on: speed === n }"
        @click="setSpeed(n)"
      >
        {{ n === 0 ? '瞬间' : `${n}x` }}
      </button>
      <button type="button" @click="resetCurrent">重置本卡</button>
      <button type="button" @click="resetAll">清空全部</button>
    </div>
    <p v-if="active" class="sb-clock">
      {{ active.scenario.title }} · 已提交 {{ active.submitted ? `${Math.floor(active.elapsedSec)}s` : '否' }}
    </p>
    <ul>
      <li v-for="item in scenarios" :key="item.code">
        <button type="button" @click="fill(item.code)">填入</button>
        <div>
          <code>{{ item.code }}</code>
          <b>{{ item.title }}</b>
          <small>{{ item.expect }}</small>
        </div>
      </li>
    </ul>
    <p class="sb-hint">填入后走：立即兑换 → 邮箱 <code>sandbox@test.local</code> / 密码 <code>sandbox</code>。要看检查读秒把时钟切成 1x。</p>
  </aside>
  <button v-else type="button" class="sb-fab" @click="open = true">沙箱</button>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { SANDBOX_FILL_EVENT } from '../lib/sandbox/handle'
import {
  buildSandboxSnapshot,
  readSandboxSpeed,
  resetAllSandboxRuns,
  resetSandboxRun,
  SANDBOX_SCENARIOS,
  writeSandboxSpeed,
  type SandboxSnapshot,
} from '../lib/sandbox/scenarios'

const speeds = [0, 1, 3, 10]
const open = ref(false)
const speed = ref(readSandboxSpeed())
const scenarios = SANDBOX_SCENARIOS
const active = ref<SandboxSnapshot | null>(null)
const currentCode = ref('')
let timer: number | null = null

function refreshClock() {
  if (!currentCode.value) {
    active.value = null
    return
  }
  active.value = buildSandboxSnapshot({ code: currentCode.value, speed: speed.value })
}

function setSpeed(n: number) {
  speed.value = writeSandboxSpeed(n)
  refreshClock()
}

function fill(code: string) {
  currentCode.value = code
  resetSandboxRun(code)
  refreshClock()
  window.dispatchEvent(new CustomEvent(SANDBOX_FILL_EVENT, { detail: { code } }))
}

function resetCurrent() {
  if (currentCode.value) resetSandboxRun(currentCode.value)
  refreshClock()
}

function resetAll() {
  resetAllSandboxRuns()
  refreshClock()
}

onMounted(() => {
  timer = window.setInterval(refreshClock, 1000)
})
onUnmounted(() => {
  if (timer) window.clearInterval(timer)
})
</script>

<style scoped>
.sb-fab,
.sb-x,
.sb-tools button,
ul button {
  border: 1px solid var(--brd, #d1d5db);
  background: var(--surface, #fff);
  color: var(--ink, #111827);
  border-radius: 8px;
  cursor: pointer;
}
.sb-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 80;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
}
.sb-panel {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 80;
  width: min(420px, calc(100vw - 24px));
  max-height: min(72vh, 640px);
  overflow: auto;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--brd, #d1d5db);
  background: var(--surface, #fff);
  box-shadow: 0 16px 40px rgb(0 0 0 / 16%);
  font-size: 12px;
  line-height: 1.45;
}
.sb-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.sb-head span {
  color: var(--muted, #6b7280);
  flex: 1;
}
.sb-x {
  padding: 2px 8px;
}
.sb-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}
.sb-tools button {
  padding: 2px 8px;
}
.sb-tools button.on {
  background: var(--primary, #0f766e);
  color: var(--primary-on, #fff);
  border-color: transparent;
}
.sb-clock {
  margin: 0 0 8px;
  color: var(--ink-2, #4b5563);
}
ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}
li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: start;
}
ul button {
  padding: 6px 8px;
  font-weight: 700;
}
code {
  display: block;
  font-size: 11px;
}
b {
  display: block;
}
small {
  display: block;
  color: var(--muted, #6b7280);
}
.sb-hint {
  margin: 10px 0 0;
  color: var(--muted, #6b7280);
}
</style>
